/**
 * Shared positional-index tracked-change tagging for DOCX viewers.
 *
 * Both the `docx-preview` and SuperDoc render paths need to stamp
 * `data-w-kind` / `data-w-id` attributes on rendered `<ins>` and `<del>`
 * elements so that `EditCard` and the backend accept/reject flow can target
 * them by tracked-change ID. The tagging relies on positional order matching
 * the backend's `trackedChangeIds` response — a guarantee that holds for
 * `docx-preview` (controlled by this codebase) but should be audited for
 * third-party renderers before production use.
 */
export function tagTrackedChanges(
  root: HTMLElement,
  ids: Array<{ kind: "ins" | "del"; w_id: string }>,
) {
  const insNodes = Array.from(root.querySelectorAll<HTMLElement>("ins"));
  const delNodes = Array.from(root.querySelectorAll<HTMLElement>("del, s, strike"));

  const expectedIns = ids.filter((i) => i.kind === "ins").length;
  const expectedDel = ids.filter((i) => i.kind === "del").length;
  if (insNodes.length !== expectedIns || delNodes.length !== expectedDel) {
    console.warn(
      "[tagTrackedChanges] node count mismatch — positional tagging may be inaccurate.",
      { expectedIns, actualIns: insNodes.length, expectedDel, actualDel: delNodes.length },
    );
  }

  const counters = { ins: 0, del: 0 };

  ids.forEach((item) => {
    const source = item.kind === "ins" ? insNodes[counters.ins++] : delNodes[counters.del++];
    if (source) {
      source.dataset.wKind = item.kind;
      source.dataset.wId = item.w_id;
      source.classList.add(item.kind === "ins" ? "vadium-docx-insertion" : "vadium-docx-deletion");
    }
  });
}
